import { PortfolioList } from './PortfolioList.jsx';
import { Button } from './Button.jsx';
import { formatMoney, formatTwh, formatGw, formatPercent } from './format.js';

export function BuildGridPanel({
  state,
  slotsTotal,
  slotsUsed,
  demand,
  estimatedOutput,
  capitalThisYear,
  listedCapitalThisYear,
  resourceDevelopmentPremiumThisYear = 0,
  annualOpex,
  buildLocked = false,
  plantsById,
  firmCapacity = null,
  requiredFirmCapacity = null,
  storageCharge = null,
  decommissionThisYear = 0,
  onRemovePending,
  onRetire,
  onUnretire,
  onCommit
}) {
  const slotsLeft = slotsTotal - slotsUsed;
  const hasPlants = state.portfolio.length > 0 || state.pendingBuilds.length > 0;
  const showFirmCapacity =
    firmCapacity !== null && requiredFirmCapacity !== null;
  const underchargedStorage = (storageCharge?.storageRows ?? []).filter(
    (row) => row.level < 1
  );

  return (
    <div className="build-grid-inner">
      <div className="build-grid-header">
        <span className="build-grid-title">Your Grid</span>
        <span className="build-grid-slots-badge">
          {slotsLeft} / {slotsTotal} slots free
        </span>
      </div>

      <div className="build-grid-list">
        {hasPlants ? (
          <PortfolioList
            portfolio={state.portfolio}
            pendingBuilds={state.pendingBuilds}
            plantsById={plantsById}
            onRemovePending={onRemovePending}
            removeDisabled={buildLocked}
            pendingRetirements={state.pendingRetirements ?? []}
            onRetire={onRetire}
            onUnretire={onUnretire}
            year={state.year}
          />
        ) : (
          <div className="build-grid-empty">
            <span className="build-grid-empty-icon" aria-hidden="true">*</span>
            <p className="build-grid-empty-text">
              Select plants from the catalogue to build your grid.
            </p>
          </div>
        )}
      </div>

      <div className="build-grid-stats">
        <div className="build-grid-stat">
          <span className="build-grid-stat-label">
            {resourceDevelopmentPremiumThisYear > 0
              ? 'Listed Capital'
              : 'Total Capital'}
          </span>
          <span className="build-grid-stat-value">
            {formatMoney(
              resourceDevelopmentPremiumThisYear > 0
                ? listedCapitalThisYear
                : capitalThisYear
            )}
          </span>
        </div>
        {resourceDevelopmentPremiumThisYear > 0 ? (
          <div className="build-grid-stat">
            <span className="build-grid-stat-label">Resource Premium</span>
            <span
              className="build-grid-stat-value"
              data-testid="build-grid-resource-premium"
            >
              {formatMoney(resourceDevelopmentPremiumThisYear)}
            </span>
          </div>
        ) : null}
        {resourceDevelopmentPremiumThisYear > 0 ? (
          <div className="build-grid-stat">
            <span className="build-grid-stat-label">Total Capital</span>
            <span className="build-grid-stat-value">
              {formatMoney(capitalThisYear)}
            </span>
          </div>
        ) : null}
        <div className="build-grid-stat">
          <span className="build-grid-stat-label">Total Opex/yr</span>
          <span className="build-grid-stat-value">{formatMoney(annualOpex)}</span>
        </div>
        {decommissionThisYear > 0 ? (
          <div className="build-grid-stat">
            <span className="build-grid-stat-label">Decommissioning</span>
            <span
              className="build-grid-stat-value"
              data-testid="build-grid-decommission"
            >
              {formatMoney(decommissionThisYear)}
            </span>
          </div>
        ) : null}
        <div className="build-grid-stat">
          <span className="build-grid-stat-label">Est. Output</span>
          <span
            className={
              'build-grid-stat-value ' +
              (estimatedOutput < demand && estimatedOutput > 0 ? 'build-grid-stat-risk' : '')
            }
          >
            {formatTwh(estimatedOutput)}
          </span>
        </div>
        <div className="build-grid-stat">
          <span className="build-grid-stat-label">Demand</span>
          <span className="build-grid-stat-value">{formatTwh(demand)}</span>
        </div>
        {showFirmCapacity ? (
          <>
            <div className="build-grid-stat">
              <span className="build-grid-stat-label">Firm Capacity</span>
              <span
                className={
                  'build-grid-stat-value ' +
                  (firmCapacity < requiredFirmCapacity
                    ? 'build-grid-stat-risk'
                    : '')
                }
                data-testid="build-grid-firm-capacity"
              >
                {formatGw(firmCapacity)}
              </span>
            </div>
            <div className="build-grid-stat">
              <span className="build-grid-stat-label">Firm Needed</span>
              <span className="build-grid-stat-value">
                {formatGw(requiredFirmCapacity)}
              </span>
            </div>
          </>
        ) : null}
      </div>

      {underchargedStorage.length > 0 ? (
        <div
          className="build-grid-storage-note"
          data-testid="build-grid-storage-charge"
        >
          {underchargedStorage.map((row) => (
            <p key={row.id}>
              {row.name} will charge to {formatPercent(row.level)} and
              contribute that share of its firm capacity.{' '}
              {formatTwh(row.surplusShort)} more expected surplus would
              fill it.
            </p>
          ))}
        </div>
      ) : null}

      <div className="build-grid-actions">
        <Button
          onClick={onCommit}
          disabled={buildLocked}
          testId="build-commit"
          audioCue="none"
        >
          {slotsUsed === 0 ? 'Skip building this year' : 'Confirm build'}
        </Button>
      </div>
    </div>
  );
}
